import { useApolloClient } from '@vue/apollo-composable';
import type { SuggestedAllocation } from '@/types/composition-functions';
import type { SuggestPaymentAllocationQuery, SuggestPaymentAllocationQueryVariables } from '@/types/operations';
import { SuggestPaymentAllocationDocument } from '@/types/vue-apollo-helpers';
import { toUserMessage } from '@/utils/error';

/**
 * The server's suggested split of a payment across a student's unpaid fees.
 *
 * Each call asks the network: the lazy-query version only ran on its first
 * `load()`, so every later request silently returned the first answer.
 */
export const useSuggestPaymentAllocation = () => {
  const { client } = useApolloClient();
  const suggestions = ref<SuggestedAllocation[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const suggest = async(amount: number, student_id: string): Promise<SuggestedAllocation[]> => {
    loading.value = true;
    error.value = null;
    try {
      const { data } = await client.query<SuggestPaymentAllocationQuery, SuggestPaymentAllocationQueryVariables>({
        query: SuggestPaymentAllocationDocument,
        variables: { amount, student_id },
        fetchPolicy: 'network-only',
      });
      suggestions.value = (data?.suggestPaymentAllocation ?? []) as SuggestedAllocation[];
    } catch (e) {
      suggestions.value = [];
      error.value = toUserMessage(e, 'We couldn\'t split the payment automatically. Pick the fees yourself.');
      throw e;
    } finally {
      loading.value = false;
    }
    return suggestions.value;
  };

  return {
    suggestions,
    loading,
    error,
    suggest,
  };
};
